import React from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Modal, Spin, Tabs, Popover, Input, Checkbox, message } from 'antd';
import { PATH } from 'constants/path';
import s from './s.module.less';
import commonS from 'styles/common.module.less';
import AIChat from 'components/AIChat';
import { EAIChatPageId } from 'types/ai';
import { ProspectsIcon } from 'components/MenuIcons';
import { RightOutlined } from '@ant-design/icons';
import useProspectInfo from 'hooks/useProspectInfo';
import Message from './components/message';
import Notes from './components/notes';
import TreatmentPlan from './components/treatmentPlan';
import AddAndUpdateProspects from 'components/AddAndUpdateProspects';
import { EOnBoardingStatus } from 'types/common';
import { getUrlParam } from 'utils/common';
import ProviderStore from 'store/Provider';
import { getDisableShowDirectLinkInProspect, setDisableShowDirectLinkInProspect } from 'utils/localstore';
import { updateAppointmentLink } from 'api/practiceFront';
import SubscriptionStore from 'store/Subscription';
import useProviderHomeInfo from 'hooks/useProviderHomeInfo';
import Forms from './components/forms';
import ProsepectInfo from './components/prosepectInfo';
import { useBillingPolicy } from 'hooks/paymentSetup/useBillingPolicy';
import { useGetConnectAccountInfo } from 'hooks/useGetConnectAccountInfo';
import AppointmentList from 'components/AppointmentList';
import PrescriptionsAndPharmacy from './components/PrescriptionsAndPharmacy';
import Billing from './components/Billing';
import Documents from './components/DocumentTab';

const Page = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const isGroupPractice = location.pathname.includes('group-practice');
    const parsedId = id ? parseInt(id, 10) : undefined;
    const [data, loading, run] = useProspectInfo(parsedId, isGroupPractice);
    const [showEdit, setShowEdit] = React.useState(false);
    const [upcomingAmount, setUpcomingAmount] = React.useState('');
    const [showEnterDirectLink, setShowEnterDirectLink] = React.useState(false);
    const [disableShow, setDisableShow] = React.useState(false);
    const [integrationUrl, setIntegrationUrl] = React.useState('');
    const disableShowLocal = getDisableShowDirectLinkInProspect();

    const [getUser] = ProviderStore.useStore();
    const [getSubscription] = SubscriptionStore.useStore();
    const user = getUser('data');
    const loadingUser = getUser('loading');
    const loadingPlan = getSubscription('loading');
    const hasPlan = true; //!isFreeUser();
    const [homeData, loadingHome] = useProviderHomeInfo();
    const [billingPolicy, loadingBillingPolicy] = useBillingPolicy(false, data?.providerId);
    const [connectAccountInfo, loadingConnectAccountInfo] = useGetConnectAccountInfo();

    const hasLoad = user?.email;
    let directLink = user?.appointmentIntegrationUrl;
    if (directLink?.startsWith('http') && directLink.indexOf('://') > -1) {
        directLink = directLink.substring(directLink.indexOf('://') + 3);
    }

    const resetDirectLinkModal = () => {
        setIntegrationUrl('');
    };

    React.useEffect(() => {
        if (!homeData) {
            return;
        }
        if (hasLoad && !directLink && !disableShowLocal && !homeData.ppaProvider && !homeData.freeUser) {
            // 不是ppa user 并且不是free user才弹弹窗
            setShowEnterDirectLink(true);
        }
    }, [hasLoad, user, directLink, disableShowLocal, homeData]);

    const items = [
        {
            key: '1',
            label: <div>Visits</div>,
            children: (
                <AppointmentList
                    title="Visits"
                    prospect={data}
                    showCreateVisitAction={!isGroupPractice}
                    isGroupPractice={isGroupPractice}
                />
            ),
        },
        {
            key: '2',
            label: <div>Message</div>,
            children: (
                <Message homeInfo={homeData} prospect={data} hasBanner={!hasPlan} isGroupPractice={isGroupPractice} />
            ),
        },
        {
            key: '3',
            label: <div>Intake & Visit Notes</div>,
            children: <TreatmentPlan prospect={data} isGroupPractice={isGroupPractice} />,
        },
        {
            key: '4',
            label: <div>Prescriptions & Pharmacy</div>,
            children: <PrescriptionsAndPharmacy prospectId={data?.id} isGroupPractice={isGroupPractice} />,
            hide: !data?.prescribed,
        },
        {
            key: '5',
            label: <div>Billing</div>,
            children: (
                <Billing
                    clientId={isGroupPractice ? data?.identityId : data?.id}
                    state={data?.state}
                    isGroupPractice={isGroupPractice}
                />
            ),
        },
        {
            key: '6',
            label: <div>Documents</div>,
            children: (
                <Documents clientId={isGroupPractice ? data?.identityId : data?.id} isGroupPractice={isGroupPractice} />
            ),
        },
        {
            key: '7',
            label: <div>Notes</div>,
            children: <Notes prospect={data} hasBanner={!hasPlan} isGroupPractice={isGroupPractice} />,
        },
        {
            key: '8',
            label: <div>Forms</div>,
            children: <Forms prospect={data} hasPlan={hasPlan} isGroupPractice={isGroupPractice} />,
        },
    ];
    const tabs = items.filter((item) => !item.hide);
    const name = `${data?.firstName || ''} ${data?.lastName || ''}`;

    const handleOk = async () => {
        if (integrationUrl) {
            let url = integrationUrl;
            if (!url.startsWith('http')) {
                url = `https://${url}`;
            }
            const result = await updateAppointmentLink({
                appointmentIntegrationUrl: url,
                useIntegrationUrl: true,
            });
            if (!result.error) {
                message.success('Direct booking link updated successfully');
                setShowEnterDirectLink(false);
                // setUser('data', {
                //     ...user,
                //     appointmentIntegrationUrl: url,
                // });
            }
        } else {
            //checked
            setDisableShowDirectLinkInProspect('true');
            setShowEnterDirectLink(false);
        }
    };

    //todo
    const isSample = data?.isSample;

    let patientPortal = 'Not sent';
    if (data?.onBoardingStatus === EOnBoardingStatus.SENT) {
        patientPortal = 'Sent';
    } else if (data?.onBoardingStatus === EOnBoardingStatus.COMPLETE) {
        patientPortal = 'Signed up';
    } else if (data?.onBoardingStatus === EOnBoardingStatus.IN_PROGRESS) {
        patientPortal = 'In progress';
    }

    const cNumber = data?.cardNum || data?.patientCardNum;
    const cCvc = data?.cardCvc || data?.patientCardCvc;

    return (
        <div className={s.wrap}>
            <Spin spinning={loading || loadingBillingPolicy || loadingConnectAccountInfo}>
                <div className={s.header}>
                    <div className={s.headerTop}>
                        <span className={s.leftTopHome}>{ProspectsIcon}</span>
                        <span
                            onClick={() => {
                                navigate(`${PATH.DASHBOARD}/${PATH.PROSPECTS_MANAGE}`);
                            }}
                            className={s.link}
                        >
                            Clients
                        </span>
                        <span className={s.leftTopNav}>
                            <RightOutlined />
                        </span>
                        <span className={s.leftTopName}>{name}</span>
                    </div>
                </div>
                {/* https://app.asana.com/1/1201608580743313/project/1203117087311346/task/1210475638082476?focus=true */}
                {/* {
                    !hasPlan && (
                        <div className={s.bannerWrap}>
                            <KiwiUpgradeBanner />
                        </div>
                    )
                } */}
                <div className={`${s.content} ${!hasPlan ? s.contentWithBanner : ''}`}>
                    <div className={s.contentLeft}>
                        <ProsepectInfo
                            connectAccountInfo={connectAccountInfo}
                            data={data}
                            loading={loading}
                            onUpdate={run}
                            billingPolicy={billingPolicy}
                            hasPlan={hasPlan}
                            isGroupPractice={isGroupPractice}
                            showEligibility
                        />
                    </div>
                    <div className={s.contentRight}>
                        <Tabs defaultActiveKey={getUrlParam('tab') || '1'} items={tabs} onChange={() => {}} />
                    </div>
                </div>
            </Spin>
            {showEdit && (
                <AddAndUpdateProspects
                    item={data}
                    show={showEdit}
                    isGroupPractice={isGroupPractice}
                    onSuccess={() => {
                        run();
                        setShowEdit(false);
                    }}
                    onCannel={() => {
                        setShowEdit(false);
                    }}
                />
            )}
            <Modal
                open={showEnterDirectLink}
                title="Enter your direct booking link here"
                width="550px"
                okText="Save"
                onOk={handleOk}
                destroyOnClose
                onCancel={() => {
                    setShowEnterDirectLink(false);
                    resetDirectLinkModal();
                }}
                okButtonProps={{
                    loading: false,
                    disabled: !disableShow && !integrationUrl,
                }}
            >
                <div className={s.modalWrap}>
                    <div className={s.info}>
                        To enhance patient convenience, please provide your direct booking link. This link will be
                        included in auto-reply emails for easy appointment scheduling.
                    </div>
                    <div className={s.inputLabel}>
                        <span style={{ marginRight: 4 }}>Scheduling link</span>
                        <Popover
                            content={
                                <div className={s.pop}>
                                    Please insert a URL where your patient can directly schedule an appointment with you
                                </div>
                            }
                            trigger="hover"
                            arrow={false}
                            placement="topLeft"
                        >
                            <span>
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="21"
                                    height="21"
                                    viewBox="0 0 21 21"
                                    fill="none"
                                >
                                    <path
                                        d="M7.1992 8C7.67968 7.02901 8.97612 6.33333 10.5 6.33333C12.433 6.33333 14 7.45262 14 8.83333C14 9.99953 12.8821 10.9792 11.37 11.2555C10.8954 11.3422 10.5 11.7064 10.5 12.1667M10.5 14.6667H10.5088M18.375 10.5C18.375 14.6421 14.8492 18 10.5 18C6.15076 18 2.625 14.6421 2.625 10.5C2.625 6.35786 6.15076 3 10.5 3C14.8492 3 18.375 6.35786 18.375 10.5Z"
                                        stroke="#6B7280"
                                        strokeWidth="1.5"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                </svg>
                            </span>
                        </Popover>
                    </div>
                    <div className={s.inputVal}>
                        <Input
                            onChange={(e) => {
                                setIntegrationUrl(e.target.value);
                            }}
                            addonBefore="https://"
                            placeholder="mybooklink.com"
                            value={integrationUrl || directLink || ''}
                        />
                    </div>
                    <div className={s.checkWrap}>
                        <Checkbox
                            onChange={(e) => {
                                setDisableShow(e.target.checked);
                            }}
                        >
                            <p className={s.text}>Don&apos;t show this again</p>
                        </Checkbox>
                    </div>
                </div>
            </Modal>
            <div className={commonS.chatWrap}>
                <AIChat pageId={EAIChatPageId.PROVIDER_CLIENTS_DETAIL} defaultShow={false} makeFix={false} />
            </div>
        </div>
    );
};

export default Page;
